/**
 * AuthContext.jsx — DeutschBridge Institute
 *
 * FIX: After login as a student, checks payment status.
 * If no completed payment exists → redirects to /payment/pending
 * (a lightweight page that shows options to pay or contact support).
 *
 * Allowed unpaid routes so students don't get stuck in a redirect loop:
 *   /payment/verify    — Paystack callback
 *   /payment/pending   — "You haven't paid yet" screen
 *   /change-password   — forced password change
 *   /register          — shouldn't be reachable but safe to allow
 */
import React, {
  createContext, useContext, useState, useEffect, useCallback,
} from 'react';
import { apiGetMe } from '../utils/api';
import API from '../utils/apiClient';

const AuthContext = createContext(null);

// ── Routes a student can visit before paying ─────────────────
const UNPAID_ALLOWED = [
  '/payment/verify',
  '/payment/pending',
  '/change-password',
  '/register',
  '/login',
];

function isUnpaidAllowed(pathname) {
  return UNPAID_ALLOWED.some(p => pathname.startsWith(p));
}

// ── Normalise the user object from any API response ──────────
function normalise(u) {
  if (!u) return u;
  const out = { ...u };
  if (u.student) {
    out.student = {
      id:               u.student.id,
      student_number:   u.student.student_number  ?? u.student.studentNumber ?? null,
      currentLevel:     u.student.current_level   ?? u.student.currentLevel   ?? 'A1',
      unlockedLevels:   parseArr(u.student.unlocked_levels  ?? u.student.unlockedLevels  ?? ['A1']),
      completedLevels:  parseArr(u.student.completed_levels ?? u.student.completedLevels ?? []),
      enrollmentStatus: u.student.enrollment_status ?? u.student.enrollmentStatus ?? 'active',
    };
    out.currentLevel    = out.student.currentLevel;
    out.unlockedLevels  = out.student.unlockedLevels;
    out.completedLevels = out.student.completedLevels;
    out.studentId       = out.student.id;
  }
  return out;
}

function parseArr(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') { try { return JSON.parse(v); } catch { return []; } }
  return [];
}

// ── Provider ─────────────────────────────────────────────────
export const AuthProvider = ({ children }) => {
  const [user, setUser]             = useState(null);
  const [loading, setLoading]       = useState(true);
  // null = not checked yet, true = has paid, false = hasn't paid
  const [hasPaid, setHasPaid]       = useState(null);
  const [paymentChecking, setPayCheck] = useState(false);

  // ── Rehydrate from localStorage on page load ─────────────
  useEffect(() => {
    const token = localStorage.getItem('goethe_token');
    if (!token) { setLoading(false); return; }

    apiGetMe()
      .then(({ user: freshUser }) => {
        const n = normalise(freshUser);
        setUser(n);
        // Check payment status for students on rehydration too
        if (n.role === 'student') {
          checkPayment(n);
        }
      })
      .catch(() => {
        localStorage.removeItem('goethe_token');
        localStorage.removeItem('goethe_user');
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Check whether student has a completed payment ─────────
  const checkPayment = useCallback(async (userObj) => {
    if (!userObj || userObj.role !== 'student') {
      setHasPaid(true); // non-students are always "paid"
      return;
    }
    setPayCheck(true);
    try {
      const { data } = await API.get('/payments/my-payments');
      const paid = (data.payments || []).some(
        p => p.status === 'paid'
      );
      setHasPaid(paid);
    } catch {
      // On error, default to allowing access (fail open)
      // to avoid locking students out due to network issues
      setHasPaid(true);
    } finally {
      setPayCheck(false);
    }
  }, []);

  // ── Login — called from LoginPage after successful API call ─
  const login = useCallback((userData) => {
    const n = normalise(userData);
    setUser(n);
    localStorage.setItem('goethe_user', JSON.stringify(n));
    // Trigger payment check for students
    if (n.role === 'student') {
      checkPayment(n);
    } else {
      setHasPaid(true);
    }
  }, [checkPayment]);

  // ── Logout ────────────────────────────────────────────────
  const logout = useCallback(() => {
    setUser(null);
    setHasPaid(null);
    localStorage.removeItem('goethe_token');
    localStorage.removeItem('goethe_user');
  }, []);

  // ── updateUser — patch user fields (e.g. clear temp_password)
  const updateUser = useCallback((patch) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem('goethe_user', JSON.stringify(next));
      return next;
    });
  }, []);

  // ── Refresh payment status (called after Paystack verify) ──
  const refreshPaymentStatus = useCallback(async () => {
    if (user?.role === 'student') {
      await checkPayment(user);
    }
  }, [user, checkPayment]);

  const isTeacher    = user?.role === 'teacher' || user?.role === 'admin';
  const isStudent    = user?.role === 'student';
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      updateUser,
      isTeacher,
      isStudent,
      isSuperAdmin,
      hasPaid,
      paymentChecking,
      refreshPaymentStatus,
      isUnpaidAllowed,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
